import { useEffect, useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  Control,
  Controller,
  useFieldArray,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLazySearchSystemUsersQuery } from '@/app/services/lookups';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSingleFlight } from '@/hooks/useSingleFlight';
import {
  type AnimalRow,
  firstFailureMessage,
  type ProcedureCatalogOption,
  procedureCatalogQueryOptions,
  useCreateAnimalProcedures,
} from '@/services/animal-management';
import type { DiagnosisOption } from '@/services/animal-management/api';
import { useAccessToken, useCurrentUser } from '@/services/auth';
import { useToast } from '@/services/toast';

import { OptionSelect, YES_NO_OPTIONS } from './AnimalFieldInputs';
import { LookupCombobox } from './LookupCombobox';
import {
  type AddProceduresFormValues,
  buildAddProceduresSchema,
  catalogForKind,
  createEmptyProcedure,
  PROCEDURE_FIELDS,
  type ProcedureKind,
  selectionProcedureKind,
  timeOptionsFor,
} from './procedure-fields';
import {
  applyProcedureResults,
  buildPendingProcedureRequests,
  procedureSaveKey,
} from './write-builders';

type ComboboxOption = { id: string; name: string };

const ENTERED_BY_SEARCH_LIMIT = 10;

type SearchableComboboxProps<T extends ComboboxOption> = {
  id: string;
  items: T[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  invalid?: boolean;
  onInputChange?: (value: string) => void;
  onValueChange?: (option: T | null) => void;
};

function SearchableCombobox<T extends ComboboxOption>({
  id,
  items,
  value,
  onChange,
  placeholder,
  invalid,
  onInputChange,
  onValueChange,
}: SearchableComboboxProps<T>) {
  const { t } = useTranslation();
  const selectedOption = items.find((option) => option.id === value);

  return (
    <Combobox<T>
      items={items}
      value={selectedOption}
      onValueChange={(option) => {
        onChange(option?.id ?? '');
        onValueChange?.(option);
      }}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => option.name}
    >
      <ComboboxInput
        id={id}
        aria-invalid={invalid}
        className="h-full w-full"
        placeholder={placeholder}
        showClear
        onChange={
          onInputChange
            ? (e: React.ChangeEvent<HTMLInputElement>) =>
                onInputChange(e.target.value)
            : undefined
        }
      />
      <ComboboxContent>
        <ComboboxEmpty>
          {t('common.noItemsFound', 'No items found.')}
        </ComboboxEmpty>
        <ComboboxList>
          {(option: T) => (
            <ComboboxItem key={option.id} value={option}>
              {option.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

type AddProceduresDialogProps = {
  open: boolean;
  selectedAnimals: AnimalRow[];
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

type UserSearchComboboxProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  currentUserOption?: ComboboxOption;
};

function UserSearchCombobox({
  id,
  value,
  onChange,
  invalid,
  currentUserOption,
}: UserSearchComboboxProps) {
  const { t } = useTranslation();
  const [triggerSearch, { data: searchedUsers }] =
    useLazySearchSystemUsersQuery();
  // Keeps the chosen user's label after the search results change.
  const [selected, setSelected] = useState<ComboboxOption | null>(null);

  const options = useMemo(() => {
    const mappedUsers: ComboboxOption[] = (searchedUsers ?? []).map((user) => ({
      id: user.id,
      name: user.fullName ?? user.internalEmailAddress ?? 'Unknown User',
    }));
    const combined = [
      ...(currentUserOption ? [currentUserOption] : []),
      ...(selected ? [selected] : []),
      ...mappedUsers,
    ];
    const seen = new Set<string>();
    return combined.filter((option) => {
      if (seen.has(option.id)) return false;
      seen.add(option.id);
      return true;
    });
  }, [currentUserOption, searchedUsers, selected]);

  return (
    <SearchableCombobox
      id={id}
      items={options}
      value={value}
      onChange={onChange}
      onValueChange={setSelected}
      placeholder={t(
        'animalManagement.addProcedures.enteredByPlaceholder',
        'Search user...',
      )}
      invalid={invalid}
      onInputChange={(search) => {
        if (search.length >= 1) {
          triggerSearch({ search, selectionLimit: ENTERED_BY_SEARCH_LIMIT });
        }
      }}
    />
  );
}

type NumberFieldInputProps = {
  id: string;
  value: number | null;
  onChange: (value: number | null) => void;
  invalid?: boolean;
  step?: string;
};

function NumberFieldInput({
  id,
  value,
  onChange,
  invalid,
  step = 'any',
}: NumberFieldInputProps) {
  return (
    <Input
      id={id}
      type="number"
      inputMode="decimal"
      min={0}
      step={step}
      value={value ?? ''}
      onChange={(e) =>
        onChange(
          Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber,
        )
      }
      aria-invalid={invalid}
    />
  );
}

type ProcedureCardProps = {
  kind: ProcedureKind;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  control: Control<AddProceduresFormValues>;
  form: UseFormReturn<AddProceduresFormValues>;
  procedureCatalogOptions: ProcedureCatalogOption[];
  currentUserOption?: ComboboxOption;
};

function ProcedureCard({
  kind,
  index,
  canRemove,
  onRemove,
  control,
  form,
  procedureCatalogOptions,
  currentUserOption,
}: ProcedureCardProps) {
  const { t } = useTranslation();
  const fields = PROCEDURE_FIELDS[kind];
  const name = <K extends keyof AddProceduresFormValues['procedures'][number]>(
    key: K,
  ) => `procedures.${index}.${key}` as const;
  const setIfEmpty = (
    key: 'drugId' | 'drugLabel' | 'unit' | 'procedureCatalogId' | 'description',
    value: string | null | undefined,
  ) => {
    if (value && !form.getValues(name(key)))
      form.setValue(name(key), value, {
        shouldDirty: true,
        shouldValidate: true,
      });
  };

  const performerLabel = {
    donor: t('animalManagement.addProcedures.vet', 'Vet/Vet Tech'),
    recipient: t('animalManagement.addProcedures.herdsman', 'Herdsman'),
    calf: t('animalManagement.addProcedures.who', 'Who'),
  }[kind];

  return (
    <Card size="sm" className="gap-2">
      <CardHeader>
        <CardTitle>
          {t('animalManagement.addProcedures.procedureNumber', {
            defaultValue: 'Procedure {index}',
            index: index + 1,
          })}
        </CardTitle>
        {canRemove && (
          <CardAction>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onRemove}
              aria-label={t('animalManagement.addProcedures.removeProcedure', {
                defaultValue: 'Remove procedure {index}',
                index: index + 1,
              })}
            >
              <XIcon className="size-4" />
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        {fields.diagnosis && (
          <Controller
            name={name('diagnosisId')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field
                data-invalid={fieldState.invalid}
                className="md:col-span-2"
              >
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.addProcedures.diagnosis', 'Diagnosis')}
                </FieldLabel>
                <LookupCombobox
                  id={ctrl.name}
                  kind="diagnosis"
                  value={ctrl.value || null}
                  selectedLabel={form.getValues(name('diagnosisLabel'))}
                  onChange={(id) => ctrl.onChange(id ?? '')}
                  onSelectOption={(option) => {
                    form.setValue(name('diagnosisLabel'), option?.name ?? '');
                    if (!option) return;
                    // Diagnoses carry default treatment; fill only what is still blank.
                    const diagnosis = option as DiagnosisOption;
                    setIfEmpty('drugLabel', diagnosis.drugName);
                    setIfEmpty('drugId', diagnosis.drugId);
                    setIfEmpty('unit', diagnosis.unit);
                    if (
                      diagnosis.dose != null &&
                      form.getValues(name('dose')) == null
                    )
                      form.setValue(name('dose'), diagnosis.dose, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    const defaultProcedure = procedureCatalogOptions.find(
                      (o) => o.id === diagnosis.defaultProcedureId,
                    );
                    if (defaultProcedure) {
                      setIfEmpty('description', defaultProcedure.description);
                      setIfEmpty('procedureCatalogId', defaultProcedure.id);
                    }
                  }}
                  placeholder={t(
                    'animalManagement.addProcedures.diagnosisPlaceholder',
                    'Search diagnosis...',
                  )}
                  invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        )}

        <Controller
          name={name('procedureCatalogId')}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid} className="md:col-span-2">
              <FieldLabel htmlFor={ctrl.name}>
                {t(
                  'animalManagement.addProcedures.procedureCodeName',
                  'Procedure Code - Name',
                )}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <SearchableCombobox
                id={ctrl.name}
                items={procedureCatalogOptions}
                value={ctrl.value}
                onChange={ctrl.onChange}
                placeholder={t(
                  'animalManagement.addProcedures.procedurePlaceholder',
                  'Select from catalog...',
                )}
                invalid={fieldState.invalid}
                onValueChange={(option) => {
                  if (option && fields.description) {
                    form.setValue(name('description'), option.description, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        {fields.description && (
          <Controller
            name={name('description')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field
                data-invalid={fieldState.invalid}
                className="md:col-span-2"
              >
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.columns.description', 'Description')}
                </FieldLabel>
                <Textarea
                  {...ctrl}
                  id={ctrl.name}
                  aria-invalid={fieldState.invalid}
                  placeholder={t(
                    'animalManagement.addProcedures.descriptionPlaceholder',
                    'Procedure description...',
                  )}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        )}

        <Controller
          name={name('enteredById')}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {performerLabel}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <UserSearchCombobox
                id={ctrl.name}
                value={ctrl.value}
                onChange={ctrl.onChange}
                invalid={fieldState.invalid}
                currentUserOption={currentUserOption}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        {fields.technician && (
          <Controller
            name={name('technicianId')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.addProcedures.technician', 'Technician')}
                </FieldLabel>
                <UserSearchCombobox
                  id={ctrl.name}
                  value={ctrl.value}
                  onChange={ctrl.onChange}
                  invalid={fieldState.invalid}
                  currentUserOption={currentUserOption}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        )}

        <Controller
          name={name('date')}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {t('common.date', 'Date')}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <DatePicker
                id={ctrl.name}
                value={ctrl.value}
                onChange={ctrl.onChange}
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name={name('time')}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {t('animalManagement.addProcedures.time', 'Time')}
              </FieldLabel>
              <SearchableCombobox
                id={ctrl.name}
                items={timeOptionsFor(kind)}
                value={ctrl.value}
                onChange={ctrl.onChange}
                placeholder={t(
                  'animalManagement.addProcedures.timePlaceholder',
                  'Select time...',
                )}
                invalid={fieldState.invalid}
              />
            </Field>
          )}
        />

        {fields.owner && (
          <Controller
            name={name('ownerId')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.columns.owner', 'Owner')}
                </FieldLabel>
                <LookupCombobox
                  id={ctrl.name}
                  kind="owner"
                  value={ctrl.value || null}
                  onChange={(id) => ctrl.onChange(id ?? '')}
                  placeholder={t(
                    'animalManagement.addProcedures.currentOwner',
                    "Donor's current owner",
                  )}
                  invalid={fieldState.invalid}
                />
              </Field>
            )}
          />
        )}

        {fields.pregnant && (
          <Controller
            name={name('pregnant')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.columns.pregnant', 'Pregnant')}
                </FieldLabel>
                <OptionSelect
                  id={ctrl.name}
                  options={YES_NO_OPTIONS}
                  value={ctrl.value}
                  onChange={ctrl.onChange}
                  invalid={fieldState.invalid}
                />
              </Field>
            )}
          />
        )}

        <Controller
          name={name('drugId')}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {t('animalManagement.addProcedures.drug', 'Drug')}
              </FieldLabel>
              <LookupCombobox
                id={ctrl.name}
                kind="drug"
                value={ctrl.value || null}
                selectedLabel={form.getValues(name('drugLabel'))}
                onChange={(id) => ctrl.onChange(id ?? '')}
                onSelectOption={(option) =>
                  form.setValue(name('drugLabel'), option?.name ?? '')
                }
                placeholder={t(
                  'animalManagement.addProcedures.drugPlaceholder',
                  'Search drug...',
                )}
                invalid={fieldState.invalid}
              />
            </Field>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name={name('dose')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.addProcedures.dose', 'Dose')}
                </FieldLabel>
                <NumberFieldInput
                  id={ctrl.name}
                  value={ctrl.value}
                  onChange={ctrl.onChange}
                  invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name={name('unit')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.addProcedures.unit', 'Unit')}
                </FieldLabel>
                <Input
                  {...ctrl}
                  id={ctrl.name}
                  aria-invalid={fieldState.invalid}
                  onChange={(e) => {
                    ctrl.onChange(e);
                    void form.trigger(ctrl.name);
                  }}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        </div>

        <Controller
          name={name('weight')}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {t('animalManagement.columns.weight', 'Weight')} (lbs)
              </FieldLabel>
              <NumberFieldInput
                id={ctrl.name}
                value={ctrl.value}
                onChange={ctrl.onChange}
                invalid={fieldState.invalid}
                step={fields.wholeWeight ? '1' : 'any'}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        {fields.temperature && (
          <Controller
            name={name('temperature')}
            control={control}
            render={({ field: ctrl, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={ctrl.name}>
                  {t('animalManagement.addProcedures.temperature', 'Temp')} (°F)
                </FieldLabel>
                <NumberFieldInput
                  id={ctrl.name}
                  value={ctrl.value}
                  onChange={ctrl.onChange}
                  invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function AddProceduresDialog({
  open,
  selectedAnimals,
  onOpenChange,
  onSuccess,
}: AddProceduresDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const kind = selectionProcedureKind(selectedAnimals);
  const schema = useMemo(
    () => buildAddProceduresSchema(kind ?? 'donor', t),
    [kind, t],
  );
  const { data: currentUser } = useCurrentUser();
  const token = useAccessToken();
  const { data: allProcedureCatalogOptions = [] } = useQuery(
    procedureCatalogQueryOptions(token),
  );
  const procedureCatalogOptions = useMemo(
    () => (kind ? catalogForKind(allProcedureCatalogOptions, kind) : []),
    [allProcedureCatalogOptions, kind],
  );
  const toast = useToast();
  const createProcedures = useCreateAnimalProcedures();
  const { run: singleFlight, busy: saving } = useSingleFlight();

  const currentUserOption = useMemo<ComboboxOption | undefined>(() => {
    if (!currentUser?.userId) return undefined;
    return {
      id: currentUser.userId,
      name: currentUser.displayName ?? currentUser.email,
    };
  }, [currentUser?.userId, currentUser?.displayName, currentUser?.email]);

  const form = useForm<AddProceduresFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      procedures: [createEmptyProcedure(currentUserOption?.id)],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'procedures',
  });

  // (row, animal) pairs already created, so a retry after a partial failure
  // only sends what failed.
  const [savedProcedures, setSavedProcedures] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [form, open, selectedAnimals]);

  // Backfill empty enteredById fields when currentUser resolves async
  useEffect(() => {
    if (!open || !currentUserOption?.id) return;

    const procedures = form.getValues('procedures');
    let hasChanges = false;

    procedures.forEach((procedure, index) => {
      if (!procedure.enteredById) {
        form.setValue(`procedures.${index}.enteredById`, currentUserOption.id, {
          shouldValidate: true,
        });
        hasChanges = true;
      }
    });

    if (hasChanges) {
      form.trigger('procedures');
    }
  }, [open, currentUserOption?.id, form]);

  const handleOpenChange = (next: boolean) => {
    if (!next) setSavedProcedures(new Set());
    onOpenChange(next);
  };

  const totalRecords = fields.reduce(
    (count, field) =>
      count +
      selectedAnimals.filter(
        (animal) => !savedProcedures.has(procedureSaveKey(field.id, animal.id)),
      ).length,
    0,
  );

  const submitProcedures = async (values: AddProceduresFormValues) => {
    const rowKeys = fields.map((field) => field.id);
    const requests = buildPendingProcedureRequests(
      selectedAnimals,
      values.procedures.map((procedure, index) => ({
        ...procedure,
        rowKey: rowKeys[index],
      })),
      currentUserOption?.id,
      savedProcedures,
    );
    const results = await createProcedures.mutateAsync(requests);
    const succeeded = results.filter((r) => r.success).length;
    const { saved, completedRowKeys, failures } = applyProcedureResults(
      selectedAnimals,
      rowKeys,
      requests,
      results,
      savedProcedures,
    );
    setSavedProcedures(saved);

    if (failures.size === 0) {
      toast.show({
        severity: 'success',
        summary: t('common.success', 'Success'),
        detail: t('animalManagement.writes.proceduresCreated', {
          defaultValue: '{count} procedure record(s) created.',
          count: succeeded,
        }),
      });
      handleOpenChange(false);
    } else {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t('animalManagement.writes.partialProcedures', {
          defaultValue:
            '{succeeded} of {total} procedure records created. {message}',
          succeeded,
          total: results.length,
          message: firstFailureMessage(results) ?? '',
        }),
        life: 10000,
      });
      // Drop rows that are fully saved and flag the ones that still need fixing.
      remove(completedRowKeys.map((rowKey) => rowKeys.indexOf(rowKey)));
      const remainingRowKeys = rowKeys.filter(
        (rowKey) => !completedRowKeys.includes(rowKey),
      );
      failures.forEach((message, rowKey) => {
        form.setError(
          `procedures.${remainingRowKeys.indexOf(rowKey)}.procedureCatalogId`,
          { type: 'server', message },
        );
      });
    }
    if (succeeded > 0) onSuccess();
  };
  const onSubmit = singleFlight(submitProcedures);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('animalManagement.addProcedures.title', 'Add Procedures')}
          </DialogTitle>
          <DialogDescription>
            {t('animalManagement.addProcedures.description', {
              defaultValue: 'Add procedures to {count} selected animals',
              count: selectedAnimals.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody
          as="form"
          id={formId}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <Card className="border-blue-200 bg-blue-50/70">
            <CardHeader>
              <CardTitle>
                {t('animalManagement.addProcedures.selectedAnimals', {
                  defaultValue: 'Selected Animals ({count})',
                  count: selectedAnimals.length,
                })}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {selectedAnimals.map((animal) => (
                <div
                  key={animal.id}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1.5 text-sm text-blue-900"
                >
                  <span className="font-medium">{animal.nameTag}</span>
                  <span className="rounded bg-blue-200 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {animal.animalType}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardHeader>
              <CardTitle>
                {t(
                  'animalManagement.addProcedures.proceduresToAssign',
                  'Procedures to Assign',
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  'animalManagement.addProcedures.proceduresHelper',
                  'Add one or more procedures that will be created for each selected animal.',
                )}
              </CardDescription>
              <CardAction>
                <Button
                  type="button"
                  variant="outline"
                  className="bg-white"
                  onClick={() =>
                    append(createEmptyProcedure(currentUserOption?.id ?? ''))
                  }
                >
                  <PlusIcon className="mr-2 size-4" />
                  {t(
                    'animalManagement.addProcedures.addProcedure',
                    'Add Procedure',
                  )}
                </Button>
              </CardAction>
            </CardHeader>
          </Card>

          {kind === null && selectedAnimals.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {t(
                'animalManagement.addProcedures.mixedTypes',
                'Select animals of one type (donors, recipients or calves) to add procedures.',
              )}
            </div>
          )}

          <div className="space-y-4">
            {kind &&
              fields.map((field, index) => (
                <ProcedureCard
                  key={field.id}
                  kind={kind}
                  index={index}
                  canRemove={fields.length > 1}
                  onRemove={() => remove(index)}
                  control={form.control}
                  form={form}
                  procedureCatalogOptions={procedureCatalogOptions}
                  currentUserOption={currentUserOption}
                />
              ))}
          </div>

          <div className="rounded-lg border border-emerald-300 bg-emerald-100 px-3 py-2 text-sm text-emerald-900">
            <span className="font-semibold">
              {t('animalManagement.addProcedures.summaryLabel', 'Summary:')}
            </span>{' '}
            {t('animalManagement.addProcedures.summaryText', {
              defaultValue:
                '{procedures} procedure(s) × {animals} animal(s) = {total} total records will be created',
              animals: selectedAnimals.length,
              procedures: fields.length,
              total: totalRecords,
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            <XIcon className="mr-2 size-4" />
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={
              !form.formState.isValid ||
              kind === null ||
              selectedAnimals.length === 0 ||
              createProcedures.isPending ||
              form.formState.isSubmitting ||
              saving
            }
          >
            <CheckIcon className="mr-2 size-4" />
            {t('animalManagement.addProcedures.submit', {
              defaultValue: 'Add {count} Procedure Records',
              count: totalRecords,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
